/**
 * Application-level creation chooser for personal or team activities. Listens for the retained
 * new-activity browser event; personal creation uses TaskEditor and the task store, while team
 * creation loads membership/workspace context and calls the team activity API.
 *
 * @module apps/web/src/components/activities/NewActivityFlow
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Loader2, Plus, UsersRound } from 'lucide-react';
import {
  type AttributeDef,
  numberToPriority,
  taskToUI,
  type TeamMember,
  type TeamWithMembership,
  type TeamWorkspaceData,
} from '@fluxability/types';
import { TaskEditor, type TaskEditorSave } from '@/components/blocks/TaskEditor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { groupBundlesByProject } from '@/lib/bundleGroups';
import { useTasksStore } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';

/** Local creation-flow state: choose ownership first, or open the personal/team editor directly. */
type NewActivityMode = 'choose' | 'personal' | 'team';

/** Historical browser event name used by calendar and navigation controls to open the shared creation flow. */
const NEW_ACTIVITY_EVENT = 'flexiplanner:new-activity';

/** Optional requested editor mode carried by the shared creation event. */
type NewActivityEventDetail = {
  /** Requested ownership editor; omission opens the personal/team chooser. */
  mode?: NewActivityMode;
};

/**
 * Dispatch the historical flexiplanner:new-activity CustomEvent to open choose, personal, or team
 * mode. Safe to call without a browser; defaults to the chooser.
 */
export function openNewActivityFlow(mode: NewActivityMode = 'choose') {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<NewActivityEventDetail>(NEW_ACTIVITY_EVENT, { detail: { mode } }));
}

/**
 * Application-level creation chooser for personal or team activities. Listens for the retained
 * new-activity browser event; personal creation uses TaskEditor and the task store, while team
 * creation loads membership/workspace context and calls the team activity API.
 */
export function NewActivityFlow() {
  const [mode, setMode] = useState<NewActivityMode | null>(null);
  const addTask = useTasksStore(s => s.addTask);
  const { toast } = useToast();

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<NewActivityEventDetail>).detail;
      setMode(detail?.mode || 'choose');
    };

    window.addEventListener(NEW_ACTIVITY_EVENT, handleOpen);
    return () => window.removeEventListener(NEW_ACTIVITY_EVENT, handleOpen);
  }, []);

  const handleCreatePersonalActivity = useCallback(
    async (taskData: TaskEditorSave) => {
      try {
        const response = await api.tasks.create({
          title: taskData.title,
          description: taskData.description,
          bundle_id: taskData.bundleId,
          due_date: taskData.deadline,
          do_date: taskData.scheduledStart,
          is_flexible: taskData.customAttributes?.fixed_start !== true,
          duration_minutes: taskData.estimatedDuration,
          priority: taskData.priority ? numberToPriority(taskData.priority) : 'medium',
          state_profile: taskData.stateProfile,
          split_policy: taskData.splitPolicy,
          custom_attributes: taskData.customAttributes || {},
          prerequisites: taskData.prerequisites,
        });
        const createdTask = response.data?.task || response.data;
        if (response.success && createdTask) {
          response.data?.created_prerequisites?.forEach(step => addTask(taskToUI(step)));
          addTask(taskToUI(createdTask));
          toast({ title: 'Activity added', description: 'It is ready in your personal planner.' });
          return;
        }
        throw new Error(response.error?.message || 'Try again from the New menu.');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Try again from the New menu.';
        toast({ title: 'Activity was not added', description: message, variant: 'destructive' });
        throw error;
      }
    },
    [addTask, toast]
  );

  return (
    <>
      <NewActivityChooser
        open={mode === 'choose'}
        onClose={() => setMode(null)}
        onPersonal={() => setMode('personal')}
        onTeam={() => setMode('team')}
      />
      <TaskEditor
        open={mode === 'personal'}
        onClose={() => setMode(null)}
        onSave={handleCreatePersonalActivity}
        task={null}
      />
      <TeamActivityEditor open={mode === 'team'} onClose={() => setMode(null)} />
    </>
  );
}

/** Choose whether a new activity belongs to the personal planner or a team. Callbacks switch the owning flow; this chooser does not create an activity. */
function NewActivityChooser({
  open,
  onClose,
  onPersonal,
  onTeam,
}: {
  /** Whether the controlled dialog is visible. */
  open: boolean;
  /** Ask the owner to close the dialog. */
  onClose: () => void;
  /** Switch the creation flow to the personal activity editor. */
  onPersonal: () => void;
  /** Switch the creation flow to the team activity editor. */
  onTeam: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="!w-[calc(100vw-1rem)] !max-w-[520px] p-0">
        <DialogHeader className="border-b px-4 pb-3 pt-[calc(1rem+env(safe-area-inset-top))] pr-12 text-left sm:px-6 sm:pt-6">
          <DialogTitle>New</DialogTitle>
          <DialogDescription>Choose where this activity belongs.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 p-4 sm:p-6">
          <button
            type="button"
            className="group flex min-h-[104px] w-full items-start gap-3 rounded-md border bg-background p-4 text-left shadow-sm transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={onPersonal}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Activity className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block font-semibold">Add Activity</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Use your personal bundles, schedule fields, and custom activity fields.
              </span>
            </span>
          </button>
          <button
            type="button"
            className="group flex min-h-[104px] w-full items-start gap-3 rounded-md border bg-background p-4 text-left shadow-sm transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={onTeam}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
              <UsersRound className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block font-semibold">Add Team Activity</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Assign shared work with team bundles, assignees, and team custom fields.
              </span>
            </span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Unsaved team activity form. Duration and date remain input strings until submission; assignment and bundle use selected workspace IDs. */
type TeamActivityDraft = {
  /** Unsaved team activity title. */
  title: string;
  /** Unsaved activity notes supplied by the creator. */
  description: string;
  /** Selected member account that will own execution of the activity. */
  assigned_to_user_id: string;
  /** Selected team bundle used to group the new activity. */
  bundle_id: string;
  /** Deadline draft converted for submission when present. */
  due_date: string;
  /** Draft duration estimate in minutes. */
  duration_minutes: string;
  /** Named urgency selected for the activity. */
  priority: 'low' | 'medium' | 'high' | 'urgent';
  /** Whether to defer the estimate to the assigned member. */
  assignee_decides_duration: boolean;
};

/** Reset values for a new team activity: empty identity fields, a 45-minute estimate, medium priority, and an explicit duration decision. */
const initialTeamActivityDraft: TeamActivityDraft = {
  title: '',
  description: '',
  assigned_to_user_id: '',
  bundle_id: '',
  due_date: '',
  duration_minutes: '45',
  priority: 'medium',
  assignee_decides_duration: false,
};

/** Load available teams and the selected workspace, collect team fields and assignment, and submit a new activity through the team API. Closing resets draft fields; workspace changes reconcile the selected assignee and bundle against available choices. */
function TeamActivityEditor({ open, onClose }: { /** Whether the controlled dialog is visible. */
open: boolean; /** Ask the owner to close the dialog. */
onClose: () => void }) {
  const [teams, setTeams] = useState<TeamWithMembership[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [workspace, setWorkspace] = useState<TeamWorkspaceData | null>(null);
  const [draft, setDraft] = useState<TeamActivityDraft>(initialTeamActivityDraft);
  const [attributeValues, setAttributeValues] = useState<Record<string, string>>({});
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const addTask = useTasksStore(s => s.addTask);
  const currentUserId = useUserStore(s => s.userId);
  const { toast } = useToast();

  const activeMembers = useMemo(
    () =>
      (workspace?.members || []).filter(
        (member): member is TeamMember & { /** Linked account; present on every listed member. */ user_id: string } =>
          member.status === 'active' && Boolean(member.user_id)
      ),
    [workspace]
  );
  const teamAttributes = useMemo<AttributeDef[]>(
    () => (Array.isArray(workspace?.attributes) ? workspace.attributes : []),
    [workspace]
  );
  const activeBundles = useMemo(
    () => (workspace?.bundles || []).filter(bundle => bundle.status !== 'archived'),
    [workspace]
  );
  const activeProjects = useMemo(
    () => (workspace?.projects || []).filter(project => project.status !== 'archived'),
    [workspace]
  );
  const bundleGroups = useMemo(
    () => groupBundlesByProject(activeBundles, activeProjects),
    [activeBundles, activeProjects]
  );

  useEffect(() => {
    if (!open) return;
    setStatus('');
    void loadTeams();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStatus('');
      setDraft(initialTeamActivityDraft);
      setAttributeValues({});
    }
  }, [open]);

  useEffect(() => {
    if (!open || !selectedTeamId) return;
    setAttributeValues({});
    void loadWorkspace(selectedTeamId);
  }, [open, selectedTeamId]);

  useEffect(() => {
    if (!workspace) return;
    setDraft(current => {
      const existingAssigneeIsValid = activeMembers.some(
        member => member.user_id === current.assigned_to_user_id
      );
      const existingBundleIsValid = activeBundles.some(bundle => bundle.id === current.bundle_id);
      const nextAssignee =
        existingAssigneeIsValid
          ? current.assigned_to_user_id
          : activeMembers.find(member => member.user_id === currentUserId)?.user_id ||
            activeMembers[0]?.user_id ||
            '';
      const nextBundle = existingBundleIsValid
        ? current.bundle_id
        : activeBundles[0]?.id || '';
      return {
        ...current,
        assigned_to_user_id: nextAssignee,
        bundle_id: nextBundle,
      };
    });
  }, [workspace, activeMembers, activeBundles, currentUserId]);

  const defaultAssigneeId = useMemo(
    () =>
      activeMembers.find(member => member.user_id === currentUserId)?.user_id ||
      activeMembers[0]?.user_id ||
      '',
    [activeMembers, currentUserId]
  );
  const defaultBundleId = activeBundles[0]?.id || '';

  const loadTeams = async () => {
    setLoadingTeams(true);
    try {
      const response = await api.teams.list();
      if (!response.success) {
        setStatus(response.error?.message || 'Could not load team workspaces.');
        return;
      }
      const nextTeams = response.data || [];
      setTeams(nextTeams);
      setSelectedTeamId(current =>
        current && nextTeams.some(team => team.id === current)
          ? current
          : nextTeams[0]?.id || ''
      );
      if (nextTeams.length === 0) setWorkspace(null);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Could not load team workspaces.');
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadWorkspace = async (teamId: string) => {
    setLoadingWorkspace(true);
    setWorkspace(null);
    try {
      const response = await api.teams.get(teamId);
      if (!response.success) {
        setStatus(response.error?.message || 'Could not load the selected team workspace.');
        return;
      }
      setWorkspace(response.data ?? null);
      setStatus('');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Could not load the selected team workspace.');
    } finally {
      setLoadingWorkspace(false);
    }
  };

  const resetDraft = () => {
    setDraft(current => ({
      ...initialTeamActivityDraft,
      assigned_to_user_id: current.assigned_to_user_id || defaultAssigneeId,
      bundle_id: current.bundle_id || defaultBundleId,
    }));
    setAttributeValues({});
  };

  const createTeamActivity = async () => {
    if (!selectedTeamId) return setStatus('Choose a team workspace.');
    if (!draft.title.trim()) return setStatus('Activity title is required.');
    if (!draft.bundle_id) return setStatus('Choose a team bundle before assigning an activity.');
    if (!draft.assigned_to_user_id) return setStatus('Choose an assignee.');

    const missingTeamFields = teamAttributes
      .filter(attribute => Boolean(attribute.required))
      .filter(attribute => !String(attributeValues[attribute.attr_id] ?? '').trim())
      .map(attribute => attribute.name);
    if (missingTeamFields.length > 0) {
      return setStatus(
        `Fill required team field${missingTeamFields.length === 1 ? '' : 's'}: ${missingTeamFields.join(', ')}.`
      );
    }

    setSaving(true);
    setStatus('');
    try {
      const customAttributes = buildTeamActivityAttributes(teamAttributes, attributeValues);
      const response = await api.teams.createActivity(selectedTeamId, {
        ...draft,
        duration_minutes: draft.assignee_decides_duration ? undefined : Number(draft.duration_minutes),
        due_date: draft.due_date ? new Date(draft.due_date).toISOString() : '',
        created_from: 'new_activity_flow',
        custom_attributes: {
          ...customAttributes,
          ...(draft.assignee_decides_duration ? { assignee_decides_duration: true } : {}),
        },
      });
      if (!response.success) {
        setStatus(response.error?.message || 'Could not create team activity.');
        return;
      }
      const createdTask = response.data?.task;
      if (createdTask?.user_id && createdTask.user_id === currentUserId) {
        addTask(taskToUI(createdTask));
      }
      resetDraft();
      await loadWorkspace(selectedTeamId);
      toast({ title: 'Team activity assigned', description: 'It was added to the assignee planner.' });
      onClose();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Could not create team activity.');
    } finally {
      setSaving(false);
    }
  };

  const selectedTeam = teams.find(team => team.id === selectedTeamId);
  const readyForAssignment = Boolean(selectedTeamId && activeMembers.length > 0 && activeBundles.length > 0);

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="!flex !h-dvh !max-h-dvh !w-dvw !max-w-none !translate-y-[-50%] flex-col overflow-hidden rounded-none border-0 p-0 sm:!h-auto sm:!max-h-[calc(100dvh-2rem)] sm:!w-[calc(100vw-1rem)] sm:!max-w-[760px] sm:rounded-lg sm:border sm:p-6">
        <DialogHeader className="shrink-0 border-b bg-background/96 px-4 pb-3 pt-[calc(1rem+env(safe-area-inset-top))] pr-12 text-left backdrop-blur sm:border-0 sm:bg-transparent sm:p-0 sm:pr-8">
          <DialogTitle>Add Team Activity</DialogTitle>
          <DialogDescription>
            Assign shared work with the selected team workspace fields.
          </DialogDescription>
        </DialogHeader>

        <div className="native-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-0 sm:py-0">
          <div className="grid gap-4 py-4">
            {status && (
              <div className="rounded-md border border-amber-300/50 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100">
                {status}
              </div>
            )}

            {loadingTeams ? (
              <div className="flex min-h-32 items-center justify-center rounded-md border bg-muted/20 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading team workspaces
              </div>
            ) : teams.length === 0 ? (
              <div className="rounded-md border bg-muted/20 p-4 text-sm">
                <p className="font-medium">No team workspace found.</p>
                <p className="mt-1 text-muted-foreground">
                  Create or join a team before assigning shared activities.
                </p>
                <Button asChild variant="outline" size="sm" className="mt-3">
                  <Link to="/teams" onClick={onClose}>
                    Open Teams
                  </Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="grid gap-4 rounded-md border bg-muted/20 p-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                  <div className="grid gap-2">
                    <Label>Team workspace</Label>
                    <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map(team => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Badge variant="outline" className="w-fit rounded-md">
                    {selectedTeam?.role === 'manager' ? 'Administrator' : 'Member'}
                  </Badge>
                </div>

                {loadingWorkspace ? (
                  <div className="flex min-h-32 items-center justify-center rounded-md border bg-muted/20 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading team fields
                  </div>
                ) : (
                  <>
                    {!readyForAssignment && (
                      <div className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
                        {activeMembers.length === 0
                          ? 'Add an active team member before assigning activities.'
                          : 'Create a team bundle before assigning activities.'}
                        <Button asChild variant="outline" size="sm" className="mt-3 w-full sm:w-auto">
                          <Link
                            to={selectedTeamId ? `/teams/${encodeURIComponent(selectedTeamId)}/projects-bundles` : '/teams'}
                            onClick={onClose}
                          >
                            Open team workspace
                          </Link>
                        </Button>
                      </div>
                    )}

                    <div className="grid gap-2">
                      <Label htmlFor="newTeamActivityTitle">Title</Label>
                      <Input
                        id="newTeamActivityTitle"
                        value={draft.title}
                        onChange={event => setDraft({ ...draft, title: event.target.value })}
                        placeholder="Activity title"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="newTeamActivityDescription">Notes or source context</Label>
                      <Textarea
                        id="newTeamActivityDescription"
                        className="min-h-24"
                        value={draft.description}
                        onChange={event => setDraft({ ...draft, description: event.target.value })}
                        placeholder="Notes or source context"
                      />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <Label>Assignee</Label>
                        <Select
                          value={draft.assigned_to_user_id}
                          onValueChange={assigned_to_user_id => setDraft({ ...draft, assigned_to_user_id })}
                          disabled={activeMembers.length === 0}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Assignee" />
                          </SelectTrigger>
                          <SelectContent>
                            {activeMembers.map(member => (
                              <SelectItem key={member.id} value={member.user_id}>
                                {member.display_name || member.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label>Bundle</Label>
                        <Select
                          value={draft.bundle_id}
                          onValueChange={bundle_id => setDraft({ ...draft, bundle_id })}
                          disabled={activeBundles.length === 0}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Bundle" />
                          </SelectTrigger>
                          <SelectContent>
                            {bundleGroups.map((group, groupIndex) => (
                              <Fragment key={group.id}>
                                <SelectGroup>
                                  <SelectLabel className="pl-2 text-xs uppercase tracking-wide text-muted-foreground">
                                    {group.label}
                                  </SelectLabel>
                                  {group.bundles.map(bundle => (
                                    <SelectItem key={bundle.id} value={bundle.id}>
                                      {bundle.name}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                                {groupIndex < bundleGroups.length - 1 && <SelectSeparator />}
                              </Fragment>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="newTeamActivityDue">Due date</Label>
                        <Input
                          id="newTeamActivityDue"
                          type="datetime-local"
                          value={draft.due_date}
                          onChange={event => setDraft({ ...draft, due_date: event.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="newTeamActivityDuration">Duration minutes</Label>
                        <Input
                          id="newTeamActivityDuration"
                          type="number"
                          min="1"
                          value={draft.duration_minutes}
                          disabled={draft.assignee_decides_duration}
                          onChange={event => setDraft({ ...draft, duration_minutes: event.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Priority</Label>
                        <Select
                          value={draft.priority}
                          onValueChange={(priority: TeamActivityDraft['priority']) => setDraft({ ...draft, priority })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {teamAttributes.length > 0 && (
                      <div className="grid gap-3 rounded-md border bg-muted/20 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <h3 className="text-sm font-medium">Team custom fields</h3>
                            <p className="text-xs text-muted-foreground">
                              These values stay with the team activity.
                            </p>
                          </div>
                          <Badge variant="outline" className="rounded-md">
                            {teamAttributes.filter(attribute => Boolean(attribute.required)).length} required
                          </Badge>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {teamAttributes.map(attribute => (
                            <TeamAttributeInput
                              key={attribute.attr_id}
                              attribute={attribute}
                              value={attributeValues[attribute.attr_id] || ''}
                              onChange={value =>
                                setAttributeValues(current => ({
                                  ...current,
                                  [attribute.attr_id]: value,
                                }))
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <label className="flex items-start gap-3 rounded-md border bg-muted/30 p-3 text-sm">
                      <Checkbox
                        checked={draft.assignee_decides_duration}
                        onCheckedChange={checked =>
                          setDraft({ ...draft, assignee_decides_duration: Boolean(checked) })
                        }
                      />
                      <span>
                        <span className="block font-medium">Let assignee decide duration</span>
                        <span className="block text-xs text-muted-foreground">
                          Fluxability can infer a starter estimate and prompt the assignee before scheduling.
                        </span>
                      </span>
                    </label>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t bg-background/96 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:mt-6 sm:border-0 sm:bg-transparent sm:p-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={createTeamActivity}
            disabled={saving || loadingTeams || loadingWorkspace || teams.length === 0 || !readyForAssignment}
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Add Team Activity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Select the input for a team attribute definition: enum choices, a boolean checkbox, or a typed text/number/date field. Values remain strings until the enclosing form validates and builds its API payload. */
function TeamAttributeInput({
  attribute,
  value,
  onChange,
}: {
  /** Team field definition controlling the label, type, choices, and allowed bounds. */
  attribute: AttributeDef;
  /** Controlled field draft, kept as a string until the enclosing form parses and validates it. */
  value: string;
  /** Report the edited draft value to the owner; this callback does not itself prove a saved server result. */
  onChange: (value: string) => void;
}) {
  const label = `${attribute.name}${attribute.required ? ' *' : ''}`;
  const options = parseAttributeOptions(attribute.options);
  if (attribute.data_type === 'enum') {
    return (
      <div className="grid gap-2">
        <Label>{label}</Label>
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder={`Select ${attribute.name}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (attribute.data_type === 'boolean') {
    return (
      <label className="flex min-h-10 items-center gap-3 rounded-md border bg-background px-3 py-2 text-sm">
        <Checkbox checked={value === 'true'} onCheckedChange={checked => onChange(checked ? 'true' : 'false')} />
        <span className="min-w-0 truncate">{label}</span>
      </label>
    );
  }

  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Input
        type={attributeInputType(attribute)}
        value={value}
        onChange={event => onChange(event.target.value)}
        min={attribute.min_val ?? undefined}
        max={attribute.max_val ?? undefined}
      />
    </div>
  );
}

/** Choose number or date inputs for recognized field types; remaining attribute types use text input. */
function attributeInputType(attribute: AttributeDef): 'number' | 'date' | 'text' {
  if (['float', 'integer', 'Number'].includes(attribute.data_type)) return 'number';
  if (attribute.data_type === 'date') return 'date';
  return 'text';
}

/** Build nonempty custom-field values under both stable attribute IDs and normalized name aliases for wire compatibility. Coercion is shallow; server field validation remains authoritative. */
function buildTeamActivityAttributes(attributes: AttributeDef[] = [], values: Record<string, string>) {
  const output: Record<string, unknown> = {};
  for (const attribute of attributes) {
    const raw = values[attribute.attr_id];
    if (raw === undefined || raw === '') continue;
    const value = coerceAttributeValue(attribute, raw);
    output[attribute.attr_id] = value;
    output[normalizeAttributeKey(attribute.name)] = value;
  }
  return output;
}

/** Convert supported numeric drafts when finite and boolean drafts by the literal true value. Unparseable numbers remain strings for downstream validation rather than silently becoming zero. */
function coerceAttributeValue(attribute: AttributeDef, value: string): unknown {
  if (['float', 'integer', 'Number'].includes(attribute.data_type)) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : value;
  }
  if (attribute.data_type === 'boolean') return value === 'true';
  return value;
}

/** Convert a field name to a lowercase ASCII underscore key, trimming separator runs at both ends for compatibility aliases. */
function normalizeAttributeKey(name: unknown): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Read enum choices from arrays, JSON array strings, or comma/newline-separated legacy text. Trim empty options; malformed JSON falls through to delimited parsing. */
function parseAttributeOptions(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(option => String(option).trim()).filter(Boolean);
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(option => String(option).trim()).filter(Boolean);
  } catch {
    // Fall through to delimited parsing.
  }
  return value
    .split(/[\n,]/)
    .map(option => option.trim())
    .filter(Boolean);
}
